import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

// Helper to construct full API URL
function getApiUrl(path: string): string {
  if (path.startsWith("http")) return path;
  
  // Ensure path starts with /
  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  
  // Normalize API_BASE_URL: remove trailing slashes and duplicate /api
  let normalizedBaseUrl = API_BASE_URL || "";
  if (normalizedBaseUrl.endsWith("/")) {
    normalizedBaseUrl = normalizedBaseUrl.slice(0, -1);
  }
  
  // Remove any double /api from base URL (e.g., /api/api -> /api)
  while (normalizedBaseUrl.endsWith("/api/api")) {
    normalizedBaseUrl = normalizedBaseUrl.slice(0, -4); // Remove "/api"
  }
  
  // Prevent double /api in URL
  // If base URL ends with /api and path starts with /api, remove /api from path
  if (normalizedBaseUrl && normalizedBaseUrl.endsWith("/api") && normalizedPath.startsWith("/api")) {
    normalizedPath = normalizedPath.substring(4); // Remove "/api" prefix (4 characters)
    // Ensure it still starts with / after removing /api
    if (!normalizedPath.startsWith("/")) {
      normalizedPath = `/${normalizedPath}`;
    }
  }
  
  // IMPORTANT: Automatically add /api prefix if not present
  // Skip for portal routes, health checks, customer routes, and paths that already have /api
  // Note: /maps/ routes are API routes and need the /api prefix
  // Works for both empty base URL (relative paths) and full base URL (absolute paths)
  const shouldAddApiPrefix = 
    !normalizedPath.startsWith("/api/") && 
    !normalizedPath.startsWith("/portal/") && 
    !normalizedPath.startsWith("/health") && 
    !normalizedPath.startsWith("/customer/");
  
  if (shouldAddApiPrefix) {
    normalizedPath = `/api${normalizedPath}`;
  }
  
  // Also check if normalizedPath already contains double /api and remove it
  normalizedPath = normalizedPath.replace(/\/api\/api/g, "/api");
  
  const fullUrl = `${normalizedBaseUrl}${normalizedPath}`;
  
  // Final check: remove any double /api from the full URL
  const finalUrl = fullUrl.replace(/\/api\/api/g, "/api");
  
  return finalUrl;
}

// Normalize baseURL to prevent double /api issues
let normalizedBaseURL = API_BASE_URL || "";
if (normalizedBaseURL.endsWith("/")) {
  normalizedBaseURL = normalizedBaseURL.slice(0, -1);
}
// Remove any double /api from base URL
while (normalizedBaseURL.endsWith("/api/api")) {
  normalizedBaseURL = normalizedBaseURL.slice(0, -4);
}

// Create axios instance
export const apiClient = axios.create({
  baseURL: normalizedBaseURL,
  withCredentials: true, // IMPORTANT: Send cookies (refresh token) with requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Get access token from auth store (we'll import this after updating the store)
let getAccessToken: (() => string | null) | null = null;
let setAccessToken: ((token: string | null) => void) | null = null;

// Export function to set token getter/setter (called from auth store)
export function configureAxiosAuth(
  tokenGetter: () => string | null,
  tokenSetter: (token: string | null) => void
) {
  getAccessToken = tokenGetter;
  setAccessToken = tokenSetter;
}

// Request interceptor: Normalize URL and add access token to Authorization header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Normalize URL to prevent double /api
    if (config.url) {
      let url = config.url;
      
      // Normalize baseURL if present
      let baseURL = config.baseURL || "";
      if (baseURL.endsWith("/")) {
        baseURL = baseURL.slice(0, -1);
      }
      // Remove any double /api from base URL
      while (baseURL.endsWith("/api/api")) {
        baseURL = baseURL.slice(0, -4);
      }
      
      // IMPORTANT: Automatically add /api prefix if not present
      // Skip for portal routes, health checks, customer routes, and paths that already have /api
      // Note: /maps/ routes are API routes and need the /api prefix
      const shouldAddApiPrefix = 
        !url.startsWith("/api/") && 
        !url.startsWith("/portal/") && 
        !url.startsWith("/health") && 
        !url.startsWith("/customer/");
      
      if (shouldAddApiPrefix) {
        url = `/api${url}`;
      }
      
      // Handle case where baseURL ends with /api and url starts with /api (prevent double /api)
      if (baseURL && baseURL.endsWith("/api") && url.startsWith("/api")) {
        url = url.substring(4); // Remove "/api" prefix (4 characters)
        // Ensure it still starts with / after removing /api
        if (!url.startsWith("/")) {
          url = `/${url}`;
        }
      }
      
      // Also check if url already contains double /api and remove it
      url = url.replace(/\/api\/api/g, "/api");
      
      config.url = url;
      config.baseURL = baseURL;
    }
    
    const token = getAccessToken?.();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Auto-refresh token on 401
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If error is 401 and we haven't retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers && token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        const response = await axios.post(
          getApiUrl("/api/auth/refresh"),
          {},
          {
            withCredentials: true, // Send refresh token cookie
          }
        );

        const { accessToken } = response.data;

        if (accessToken && setAccessToken) {
          setAccessToken(accessToken);
        }

        // Update original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        processQueue(null, accessToken);

        // Retry original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear token and reject queue
        if (setAccessToken) {
          setAccessToken(null);
        }
        processQueue(refreshError, null);

        // Clear auth store
        try {
          const { useAuthStore } = await import("@/stores/authStore");
          const clearAuth = useAuthStore.getState().clearAuth;
          clearAuth();
        } catch (error) {
          console.error("[Auth] Error clearing auth:", error);
        }

        // Reject with error (components will handle it)
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 401 that didn't trigger refresh (e.g., no refresh token available)
    if (error.response?.status === 401) {
      // Clear auth store
      try {
        const { useAuthStore } = await import("@/stores/authStore");
        const clearAuth = useAuthStore.getState().clearAuth;
        clearAuth();
      } catch (authError) {
        console.error("[Auth] Error clearing auth:", authError);
      }

      // Reject with error (components will handle it)
    }

    return Promise.reject(error);
  }
);

// Export helper to make it easy to construct full URLs
export { getApiUrl };

